import React, { useState, useEffect } from "react";
import { toast } from "sonner";
import { Loader2, Save, Upload, FileCheck2, Trash2, FileText as FileTextIcon } from "lucide-react";
import { api } from "../../lib/api";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { Label } from "../ui/label";

// Phase 101ge (Feb 2026) — Section « Formulaires administratifs VS »
//   - Propriétaire des distributeurs (Police du Commerce)
//   - Responsable sécurité alimentaire (SCAV, art. 73 ODAIOUS)
//   - Upload de l'annexe SCAV scannée (classification activités A→E,
//     fusionnée automatiquement avec chaque PDF SCAV généré)

export default function ScavCantonSection({ form, setForm, save, saving }) {
  const [annexInfo, setAnnexInfo] = useState(null); // { pages, size_kb } | null
  const [uploading, setUploading] = useState(false);

  // Détecte si une annexe est déjà stockée en pingant /preview.pdf
  useEffect(() => {
    const base = (process.env.REACT_APP_BACKEND_URL || "").replace(/\/$/, "") || window.location.origin;
    fetch(`${base}/api/settings/scav-annex/preview.pdf`, { method: "HEAD", credentials: "include" })
      .then((r) => {
        if (r.ok) setAnnexInfo({ exists: true });
        else setAnnexInfo({ exists: false });
      })
      .catch(() => setAnnexInfo({ exists: false }));
  }, []);

  const previewAnnex = () => {
    const base = (process.env.REACT_APP_BACKEND_URL || "").replace(/\/$/, "") || window.location.origin;
    window.open(`${base}/api/settings/scav-annex/preview.pdf?t=${Date.now()}`, "_blank", "noopener,noreferrer");
  };

  const uploadAnnex = async (ev) => {
    const file = ev.target.files?.[0];
    if (!file) return;
    if (file.size > 5 * 1024 * 1024) {
      toast.error("PDF > 5 MB — compresser ou scanner en 150 DPI");
      return;
    }
    const fd = new FormData();
    fd.append("file", file);
    setUploading(true);
    try {
      const r = await api.post("/settings/scav-annex", fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      setAnnexInfo({ exists: true, pages: r.data.pages, size_kb: r.data.size_kb });
      toast.success(`Annexe SCAV stockée (${r.data.pages} pages, ${r.data.size_kb} KB)`);
    } catch (e) {
      toast.error(`Échec upload : ${e?.response?.data?.detail || e.message}`);
    } finally {
      setUploading(false);
      ev.target.value = "";
    }
  };

  const deleteAnnex = async () => {
    if (!window.confirm("Supprimer l'annexe SCAV stockée ? Chaque PDF SCAV n'inclura plus la classification.")) return;
    try {
      await api.delete("/settings/scav-annex");
      setAnnexInfo({ exists: false });
      toast.success("Annexe SCAV supprimée");
    } catch (e) {
      toast.error(`Erreur : ${e.message}`);
    }
  };

  return (
    <div className="bg-white border border-[#E5E5E0] rounded-lg p-6" data-testid="scav-canton-zone">
      <h2 className="text-base font-medium text-[#1A1C1A] mb-1 flex items-center gap-2">
        <FileCheck2 className="w-5 h-5 text-[#C8102E]" />
        Formulaires administratifs — Canton du Valais
      </h2>
      <p className="text-xs text-[#575A57] mb-4">
        Pré-remplissage automatique des formulaires Police du Commerce (loi 8 fév 2007) et SCAV
        (ODAIOUS art. 20/62) depuis l'écran <strong>Distributeurs</strong>.
      </p>

      {/* Propriétaire légal (Canton) */}
      <div className="mb-6">
        <h3 className="text-sm font-semibold text-[#1A1C1A] mb-2">
          Propriétaire légal des appareils (Police du Commerce)
        </h3>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <Label className="mb-1.5 block">Nom - Prénom</Label>
            <Input
              value={form.canton_owner_name || ""}
              onChange={(e) => setForm({ ...form, canton_owner_name: e.target.value })}
              placeholder="Phimrat Pongsak"
              data-testid="canton-owner-name-input"
            />
          </div>
          <div>
            <Label className="mb-1.5 block">Adresse complète</Label>
            <Input
              value={form.canton_owner_address || ""}
              onChange={(e) => setForm({ ...form, canton_owner_address: e.target.value })}
              placeholder="Route de Chermignon 1, 3978 Flanthey"
              data-testid="canton-owner-address-input"
            />
          </div>
        </div>
      </div>

      {/* Responsable sécurité alimentaire (SCAV) */}
      <div className="mb-6">
        <h3 className="text-sm font-semibold text-[#1A1C1A] mb-2">
          Responsable sécurité alimentaire (SCAV, art. 73 ODAIOUS)
        </h3>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <Label className="mb-1.5 block">Civilité</Label>
            <select
              value={form.scav_responsible_civility || ""}
              onChange={(e) => setForm({ ...form, scav_responsible_civility: e.target.value })}
              className="flex h-10 w-full rounded-md border border-[#E5E5E0] bg-white px-3 text-sm"
              data-testid="scav-civility"
            >
              <option value="">—</option>
              <option value="Madame">Madame</option>
              <option value="Monsieur">Monsieur</option>
            </select>
          </div>
          <div></div>
          <div>
            <Label className="mb-1.5 block">Nom</Label>
            <Input
              value={form.scav_responsible_last_name || ""}
              onChange={(e) => setForm({ ...form, scav_responsible_last_name: e.target.value })}
              placeholder="Giupponi"
              data-testid="scav-last-name"
            />
          </div>
          <div>
            <Label className="mb-1.5 block">Prénom</Label>
            <Input
              value={form.scav_responsible_first_name || ""}
              onChange={(e) => setForm({ ...form, scav_responsible_first_name: e.target.value })}
              placeholder="Alain"
              data-testid="scav-first-name"
            />
          </div>
          <div>
            <Label className="mb-1.5 block">Date de naissance (JJ.MM.AAAA)</Label>
            <Input
              value={form.scav_responsible_dob || ""}
              onChange={(e) => setForm({ ...form, scav_responsible_dob: e.target.value })}
              placeholder="18.11.1953"
              data-testid="scav-dob"
            />
          </div>
          <div>
            <Label className="mb-1.5 block">Téléphone</Label>
            <Input
              value={form.scav_responsible_phone || ""}
              onChange={(e) => setForm({ ...form, scav_responsible_phone: e.target.value })}
              placeholder="079 916 40 77"
              data-testid="scav-phone"
            />
          </div>
          <div>
            <Label className="mb-1.5 block">Rue, n°</Label>
            <Input
              value={form.scav_responsible_address || ""}
              onChange={(e) => setForm({ ...form, scav_responsible_address: e.target.value })}
              placeholder="Rue de Parcouret 22"
              data-testid="scav-address"
            />
          </div>
          <div>
            <Label className="mb-1.5 block">NPA, lieu</Label>
            <Input
              value={form.scav_responsible_zip_city || ""}
              onChange={(e) => setForm({ ...form, scav_responsible_zip_city: e.target.value })}
              placeholder="1964 Conthey"
              data-testid="scav-zip-city"
            />
          </div>
          <div className="md:col-span-2">
            <Label className="mb-1.5 block">E-mail</Label>
            <Input
              value={form.scav_responsible_email || ""}
              onChange={(e) => setForm({ ...form, scav_responsible_email: e.target.value })}
              placeholder="info@maison-thai.pro"
              data-testid="scav-email"
            />
          </div>
        </div>
      </div>

      <div className="flex justify-end mb-6">
        <Button onClick={save} disabled={saving} className="bg-[#1F4E3D] hover:bg-[#173B2E]" data-testid="save-scav-btn">
          {saving ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Save className="w-4 h-4 mr-2" />} Enregistrer
        </Button>
      </div>

      {/* Annexe SCAV scannée */}
      <div className="pt-4 border-t border-[#E5E5E0]">
        <h3 className="text-sm font-semibold text-[#1A1C1A] mb-2 flex items-center gap-2">
          <FileTextIcon className="w-4 h-4 text-[#8C6B2F]" />
          Annexe SCAV (classification activités A→E)
        </h3>
        <p className="text-xs text-[#575A57] mb-3">
          Scanner <strong>UNE FOIS</strong> l'annexe officielle (pages 4-8) après avoir coché les cases
          correspondant à Maison Thaï (D502 distributeur, D1 restauration, B3 pâtisserie, etc.) et uploader
          le PDF ici. Elle sera <strong>fusionnée automatiquement</strong> à la fin de chaque PDF SCAV
          généré pour un distributeur, resto ou food-truck.
        </p>
        <div className="flex items-center gap-3 flex-wrap">
          <label className="inline-flex items-center gap-2 px-4 py-2 border border-[#1F4E3D] text-[#1F4E3D] rounded-md cursor-pointer hover:bg-[#1F4E3D]/5 text-sm font-medium">
            <Upload className="w-4 h-4" />
            {uploading ? "Upload…" : (annexInfo?.exists ? "Remplacer l'annexe" : "Uploader l'annexe (PDF)")}
            <input
              type="file"
              accept="application/pdf,.pdf"
              className="hidden"
              onChange={uploadAnnex}
              disabled={uploading}
              data-testid="scav-annex-upload"
            />
          </label>
          {annexInfo?.exists && (
            <>
              <Button variant="outline" onClick={previewAnnex} data-testid="scav-annex-preview">
                <FileTextIcon className="w-4 h-4 mr-2" /> Voir annexe stockée
              </Button>
              <Button variant="outline" onClick={deleteAnnex} className="text-[#C05C47] border-[#C05C47]" data-testid="scav-annex-delete">
                <Trash2 className="w-4 h-4 mr-2" /> Supprimer
              </Button>
              <span className="text-xs text-[#1F4E3D] bg-[#1F4E3D]/10 px-2 py-1 rounded">
                ✓ Annexe stockée{annexInfo?.pages ? ` (${annexInfo.pages} pages, ${annexInfo.size_kb} KB)` : ""}
              </span>
            </>
          )}
          {annexInfo && !annexInfo.exists && (
            <span className="text-xs text-[#A3A6A3]">Aucune annexe stockée — les PDF SCAV sortent en 3 pages.</span>
          )}
        </div>
      </div>
    </div>
  );
}
